'use client'

import { Box, Stack } from '@mantine/core'
import { DataTable } from 'mantine-datatable'

import { SectionErrorBoundary } from '@/components/error/boundary'
import { GTM_EVENTS } from '@/configs/dataTrackingEvent'
import { useI18nRouter } from '@/i18n/navigation'
import { trackGtmEvent } from '@/services/dataTracking'
import { type EquityLatestPrice, useEquityLatestPrices } from '@/services/equity/prices'
import {
  type EquityAssetSort,
  type EquityScreenerAsset,
  STOCKS_PAGE_SIZE,
} from '@/services/equity/screener'
import { useStockSparklineProbe } from '@/services/equity/sparkline'

import { defaultColumnProps, useStockColumns } from './columns/configs'
import CursorPagination from './CursorPagination'
import EmptyState from './EmptyState'
import { useCursorPagedStocks } from './hooks/useCursorPagedStocks'
import StockTableSkeleton from './Skeleton'
import styles from './styles.module.css'

interface StockTableDataProps {
  // ticker symbol (e.g. "NVDA") → numeric equity-market-data asset_id, for the
  // 24H chart sparkline. Resolved server-side from the cached assets-preview list.
  readonly assetIdBySymbol: Record<string, string>
  /** Server-side sort for the asset list. Undefined = upstream default order. */
  readonly sort?: EquityAssetSort
}

export default function StockTableDataWrapper({ assetIdBySymbol, sort }: StockTableDataProps) {
  return (
    <SectionErrorBoundary>
      <StockTableData assetIdBySymbol={assetIdBySymbol} sort={sort} />
    </SectionErrorBoundary>
  )
}

function StockTableData({ assetIdBySymbol, sort }: StockTableDataProps) {
  const { records, isPending, isFetching, hasMore, loadedPageCount, currentPage, gotoPage } =
    useCursorPagedStocks(sort)

  // Batch-fetch latest prices for the visible page (one call per page; the
  // query polls on a 60s interval, so prices refresh without touching the
  // cursor-paging state).
  const assetIds = records.map(r => r.asset_id)
  const { data: latestPrices } = useEquityLatestPrices(assetIds)
  const priceByAssetId = (latestPrices ?? []).reduce<Record<string, EquityLatestPrice>>(
    (acc, p) => {
      acc[p.asset_id] = p
      return acc
    },
    {},
  )

  // Sparkline interval: probe the first visible asset's 1D series — during US
  // market hours it returns data, off-hours/weekend it is empty and the whole
  // table falls back to 5D (header label switches to "5D chart" accordingly).
  const firstAssetId = records[0] ? (assetIdBySymbol[records[0].display_symbol] ?? '') : ''
  const sparkInterval = useStockSparklineProbe(firstAssetId)

  const columns = useStockColumns(assetIdBySymbol, priceByAssetId, sparkInterval)

  const router = useI18nRouter()
  const handleRowClick = (record: EquityScreenerAsset) => {
    trackGtmEvent({
      event: GTM_EVENTS.StocksAssetClicked,
      asset_symbol: record.display_symbol,
    })
    router.push(`/price/stocks/${record.display_symbol}`)
  }

  // First load (no rows yet): render row skeletons so the trending table's
  // loading UI matches the crypto tab — the DataTable fetching overlay only
  // blurs existing rows, and there are none yet (same pattern as Baskets).
  if (records.length === 0 && isPending) {
    return <StockTableSkeleton />
  }

  if (records.length === 0 && !isPending && !isFetching) {
    return <EmptyState />
  }

  return (
    <Box pos="relative">
      <Stack gap="16px">
        <DataTable
          fetching={isPending || isFetching}
          textSelectionDisabled
          records={records}
          columns={columns}
          defaultColumnProps={defaultColumnProps}
          idAccessor="asset_id"
          /* Styling */
          classNames={{
            root: styles.root,
            header: styles.header,
          }}
          minHeight="500px"
          backgroundColor="var(--bg-app)"
          withRowBorders={false}
          onRowClick={({ record }) => handleRowClick(record)}
          highlightOnHover
          highlightOnHoverColor="var(--bg-surface-secondary)"
        />
        <CursorPagination
          currentPage={currentPage}
          loadedPageCount={loadedPageCount}
          hasMore={hasMore}
          isFetching={isFetching}
          recordsPerPage={STOCKS_PAGE_SIZE}
          onChange={page => {
            trackGtmEvent({
              event: GTM_EVENTS.StocksPaginationClicked,
              page_number: page,
            })
            gotoPage(page)
          }}
        />
      </Stack>
    </Box>
  )
}
